import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgToastService } from 'ng-angular-popup';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { BriefSectionComponent } from '../../../../project-development/tendering/project-expression-of-interest/brief-section/brief-section.component';
import { SelectedWorkPackagesComponent } from '@/app/components/projects/selected-work-packages/selected-work-packages.component';
import { CountryComponent } from '../../../../../global/inputs/country/country.component';
import { Router } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { ActivatedRoute } from '@angular/router';
import { InitiatedEoiData } from '@/app/interfaces/InitiatedEoiData.interface';
import { DateFormatService } from '@/app/services/date-format.service';
import { catchError, map, Observable, of } from 'rxjs';
import { ErrorMessages } from '@/app/enums/error-messages.enum';
import { WarningMessages } from '@/app/enums/warning-messages.enum';
import { FileUploadChipComponent } from '@/app/components/global/inputs/file-upload-chip/file-upload-chip.component';
import { FileManagerComponent } from '@/app/components/shared/file-manager/file-manager.component';

@Component({
  selector: 'app-eoi-create',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    BriefSectionComponent,
    SelectedWorkPackagesComponent,
    CountryComponent,
    MatDatepickerModule,
    FileUploadChipComponent,
    FileManagerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './eoi-create.component.html',
  styleUrl: './eoi-create.component.css',
})
export class EoiCreateComponent implements OnInit {
  @Input() inputData!: {
    eoiId: number;
    projectId: number;
    tenderingSubType: string;
  };
  initiatedEoiData: InitiatedEoiData;
  projectId!: number;
  eoiId!: number;
  formGroup: FormGroup;
  expressionData: any;
  isUpdate = false;
  countryFromMap = 'United States';
  countryId: number = 185;
  selectCountry = false;
  projectName: string;
  projectIdentifier: string;

  // Variables to handle old files data.
  oldRelevantExperienceFiles: any[] = [];
  deletedOldRelevantExperienceFilesIds: any[] = [];
  oldkeyQualificationsFiles: any[] = [];
  deletedOldkeyQualificationsFilesIds: any[] = [];
  oldTeamCompositionFiles: any[] = [];
  deletedOldTeamCompositionFilesIds: any[] = [];
  oldHealthAndSafetyFiles: any[] = [];
  deletedOldHealthAndSafetyFilesIds: any[] = [];
  oldQualityAssuranceFiles: any[] = [];
  deletedOldQualityAssuranceFilesIds: any[] = [];
  oldAdditionalInformationFiles:any[]=[];
  deletedoldAdditionalInformationFilesIds: any[] = [];

  constructor(
    private fb: FormBuilder,
    private toast: NgToastService,
    private projectsService: ProjectsService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private tenderingService: TenderingService,
    private route: ActivatedRoute,
    private dateFormatService: DateFormatService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.projectId = +this.route.snapshot.paramMap.get('projectId')!;
    this.eoiId = +this.route.snapshot.paramMap.get('eoiId')!;
    this.route.queryParams.subscribe((params) => {
      this.projectIdentifier = params['projectIdentifier'];
      this.projectName = params['projectName'];
    });
    this.formGroup = this.fb.group({
      // Applicant Details
      company_name: ['', Validators.required],
      contact_person: ['', Validators.required],
      company_reg_no: ['', Validators.required],
      contact_person_position: ['', Validators.required],
      company_vat_no: ['', Validators.required],
      email: ['', [Validators.email]],
      website: [''],
      phone: [''],
      address: [''],
      city: [''],
      state: [''],
      zip: [''],

      // Scope of Interest
      relevant_experience: [''],
      qualifications: [''],
      team_composition: [''],

      // Compliance & Policies
      health_safety_environmental_policy: [''],
      quality_assurance_cert_attach: [''],
      conflict_of_interest: [''],

      // Additional Information
      additional_information: [''],

      // Declaration
      authorized_representative: ['', Validators.required],
      authorized_representative_position: ['', Validators.required],
      signature: ['', Validators.required],
      date: ['', Validators.required],
      status: 'pending',

      relevantExperienceFileControl: new FormArray([]),
      keyQualificationsFileControl: new FormArray([]),
      teamCompositionFileControl: new FormArray([]),
      healthAndSafetyFileControl: new FormArray([]),
      qualityAssuranceFileControl: new FormArray([]),
      additionalInformationControl:new FormArray([]),
    });
    this.getInitiationEoiData();
    this.fetchExistenceData();
  }
  getSanitizedHtml(description: string) {
    if (!description) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(description);
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = parseFloat(input.value);

    if (value > 100000000000) {
      value = 100000000000;
      input.value = value.toString();
    }
  }

  prepareDataToSubmit() {
    const dataToSubmit = new FormData();

    // Dynamically append all form controls to FormData
    Object.keys(this.formGroup.controls).forEach((key) => {
      let value = this.formGroup.get(key)?.value;
      // Regular expression used to check if the string value matches a specific date-time format
      if (
        typeof value === 'string' &&
        value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/)
      ) {
        value = this.dateFormatService.formatDate(value); // Format as 'YYYY-MM-DD'
      }

      // Check if the value is a Date object and format it
      if (value instanceof Date) {
        value = this.dateFormatService.formatDate(value); // Format as 'YYYY-MM-DD'
      }

      if (value !== null && value !== undefined && value !== '') {
        dataToSubmit.append(key, value);
      }
    });

    // Files handling Start
    this.addUploadedFiles(dataToSubmit);
    this.addDeletedOldFilesIds(dataToSubmit);
    // Files handling End

    // Append additional fields
    dataToSubmit.append('eoi_id', `${this.inputData.eoiId}`);
    dataToSubmit.append('country_id', `${this.countryId}`);

    return dataToSubmit;
  }

  addUploadedFiles(submissionFormData) {
    // Adding files to FormData
    this.formGroup.value.relevantExperienceFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append('relevant_exp_attach[]', uploadedFile);
        }
      }
    );

    this.formGroup.value.keyQualificationsFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append(
            'key_qualifications_attach[]',
            uploadedFile
          );
        }
      }
    );

    this.formGroup.value.teamCompositionFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append('team_compositions_attach[]', uploadedFile);
        }
      }
    );

    this.formGroup.value.healthAndSafetyFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append('health_safety_env_attach[]', uploadedFile);
        }
      }
    );

    this.formGroup.value.qualityAssuranceFileControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append('quality_assurance_attach[]', uploadedFile);
        }
      }
    );

    // TODO: this is for additional information
    this.formGroup.value.additionalInformationControl.forEach(
      (uploadedFile: File) => {
        if (uploadedFile) {
          submissionFormData.append('additional_info_attach[]', uploadedFile);
        }
      }
    );
  }

  addDeletedOldFilesIds(submissionFormData) {
    if (this.deletedOldRelevantExperienceFilesIds?.length > 0) {
      submissionFormData.append(
        'deleted_relevant_exp_attach_ids',
        JSON.stringify(this.deletedOldRelevantExperienceFilesIds)
      );
    }
    if (this.deletedOldkeyQualificationsFilesIds?.length > 0) {
      submissionFormData.append(
        'deleted_key_qualifications_attach_ids',
        JSON.stringify(this.deletedOldkeyQualificationsFilesIds)
      );
    }
    if (this.deletedOldTeamCompositionFilesIds?.length > 0) {
      submissionFormData.append(
        'deleted_team_compositions_attach_ids',
        JSON.stringify(this.deletedOldTeamCompositionFilesIds)
      );
    }
    if (this.deletedOldHealthAndSafetyFilesIds?.length > 0) {
      submissionFormData.append(
        'deleted_health_safety_env_attach_ids',
        JSON.stringify(this.deletedOldHealthAndSafetyFilesIds)
      );
    }
    if (this.deletedOldQualityAssuranceFilesIds?.length > 0) {
      submissionFormData.append(
        'deleted_quality_assurance_attach_ids',
        JSON.stringify(this.deletedOldQualityAssuranceFilesIds)
      );
    }
    if (this.deletedoldAdditionalInformationFilesIds?.length > 0) {
      submissionFormData.append(
        'deleted_additional_information_attach_ids',
        JSON.stringify(this.deletedoldAdditionalInformationFilesIds)
      );
    }
  }

  onSubmit(): void {
    console.log('this.formGroup', this.formGroup);
    if (this.formGroup.valid) {
      const dataToSubmit = this.prepareDataToSubmit();

      this.projectsService.projectExpressions(dataToSubmit).subscribe({
        next: () => {
          this.toast.success('Application filed successfully', 'SUCCESS');
          this.handleCLose();
        },
        error: (error: any) => {
          console.error(ErrorMessages.SAVE_DATA_ERROR, error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    } else {
      // Object.keys(this.formGroup.controls).forEach(key => {
      //   const control = this.formGroup.get(key);
      //   if (control?.invalid) {
      //     console.log(`Field: ${key}, Errors:`, control.errors);
      //   }
      // });
      this.toast.warning(WarningMessages.REQUIRED_FIELDS_WARNING, 'WARNING');
      this.formGroup.markAllAsTouched();
    }
  }

  getControl(controlName: string) {
    return this.formGroup.get(controlName);
  }

  handleCountry() {
    this.selectCountry = true;
  }

  updateCountry(country: any) {
    this.countryFromMap = country.name;
    this.countryId = country.id;
  }
  getInitiationEoiData() {
    return this.tenderingService
      .getSubmittedEoiInitiationData(this.projectId, this.eoiId)
      .subscribe({
        next: (response: any) => {
          this.initiatedEoiData = response.data;
        },
        error: (error) => {
          console.error('Error fetching data:', error);
        },
      });
  }
  transformDate(date: Date | string): string {
    return this.dateFormatService.formatDate(date);
  }
  handleCLose() {
    this.router.navigate(['/eoi-requests', this.projectId], {
      queryParams: {
        projectName: this.projectName,
        projectIdentifier: this.projectIdentifier,
      },
    });
  }
  fetchExistenceData() {
    if (this.projectId) {
      this.tenderingService
        .getCurrentEoiResponse(this.projectId, this.eoiId)
        .subscribe({
          next: (response: any) => {
            if (response?.data) {
              this.formGroup.patchValue({
                company_name: response.data.company_name || '',
                contact_person: response.data.contact_person || '',
                company_reg_no: response.data.company_reg_no || '',
                contact_person_position:
                  response.data.contact_person_position || '',
                company_vat_no: response.data.company_vat_no || '',
                email: response.data.email || '',
                website: response.data.website || '',
                phone: response.data.phone || '',
                address: response.data.address || '',
                city: response.data.city || '',
                state: response.data.state || '',
                zip: response.data.zip || '',
                nature_of_service: response.data.nature_of_service || '',
                expected_start_date: response.data.expected_start_date || '',
                type_of_project: response.data.type_of_project || '',
                expected_end_date: response.data.expected_end_date || '',
                project_location: response.data.project_location || '',
                estimation_price: response.data.estimation_price || '',
                relevant_experience: response.data.relevant_experience || '',
                qualifications: response.data.qualifications || '',
                team_composition: response.data.team_composition || '',
                health_safety_environmental_policy:
                  response.data.health_safety_environmental_policy || '',
                quality_assurance_cert_attach:
                  response.data.quality_assurance_cert_attach || '',
                conflict_of_interest: response.data.conflict_of_interest || '',
                additional_information:
                  response.data.additional_information || '',
                authorized_representative:
                  response.data.authorized_representative || '',
                authorized_representative_position:
                  response.data.authorized_representative_position || '',
                signature: response.data.signature || '',
                date: response.data.date || '',
                status: response.data.status || 'pending',
              });

              // Adding old files data to variables
              this.addOldAttachmentMapping(response.data);

              this.isUpdate = true;
            }

            if (response?.data?.country_id) {
              this.getCountryData(response?.data?.country_id).subscribe(
                (countryName) => {
                  if (countryName) {
                    this.countryFromMap = countryName;
                    this.cdr.detectChanges();
                  } else {
                    console.log('No country data available for the given ID');
                  }
                }
              );
            }
          },
          error: (err) => {
            console.error(ErrorMessages.FETCH_DATA_ERROR, err);
          },
        });
    }
  }

  addOldAttachmentMapping(response) {
    if (response.relevant_experience_attachments) {
      this.oldRelevantExperienceFiles =
        response.relevant_experience_attachments;
    }
    if (response.key_qualifications_attachments) {
      this.oldkeyQualificationsFiles = response.key_qualifications_attachments;
    }
    if (response.team_composition_attachments) {
      this.oldTeamCompositionFiles = response.team_composition_attachments;
    }
    if (response.health_and_safety_attachments) {
      this.oldHealthAndSafetyFiles = response.health_and_safety_attachments;
    }
    if (response.quality_assurance_attachments) {
      this.oldQualityAssuranceFiles = response.quality_assurance_attachments;
    }
    if (response.additional_info_attach) {
      this.oldAdditionalInformationFiles = response.additional_info_attach;
    }
    console.log('res',response);
    
  }

  getCountryData(country_id: any): Observable<any> {
    return this.projectsService.getCountry(country_id).pipe(
      map((data: any) => data?.data?.name),
      catchError((error) => {
        console.error(ErrorMessages.FETCH_DATA_ERROR, error);
        return of(null);
      })
    );
  }

  /**
   * Handling File Operations - START
   */
  // Uploading/Deleting new files for site preparation
  handleNewFiles(filesData: {
    files: File[];
    controlName: string;
    isEmpty: boolean;
  }) {
    switch (filesData.controlName) {
      case 'relevantExperienceFileControl': {
        const fileArray = this.formGroup.get(
          'relevantExperienceFileControl'
        ) as FormArray;
        fileArray.clear();
        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      case 'keyQualificationsFileControl': {
        const fileArray = this.formGroup.get(
          'keyQualificationsFileControl'
        ) as FormArray;
        fileArray.clear();
        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      case 'teamCompositionFileControl': {
        const fileArray = this.formGroup.get(
          'teamCompositionFileControl'
        ) as FormArray;
        fileArray.clear();
        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      case 'healthAndSafetyFileControl': {
        const fileArray = this.formGroup.get(
          'healthAndSafetyFileControl'
        ) as FormArray;
        fileArray.clear();
        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      case 'qualityAssuranceFileControl': {
        const fileArray = this.formGroup.get(
          'qualityAssuranceFileControl'
        ) as FormArray;
        fileArray.clear();
        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }
        
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      case 'additionalInformationControl': {
        const fileArray = this.formGroup.get(
          'additionalInformationControl'
        ) as FormArray;
        fileArray.clear();
        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }
        
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }


      default:
        break;
    }
  }

  // Uploading/Deleting new files for site preparation
  handleDeletedOldFiles(OldFileData: any) {
    switch (OldFileData.controlName) {
      case 'relevantExperienceFileControl': {
        this.deletedOldRelevantExperienceFilesIds.push(OldFileData?.file?.id);
        this.oldRelevantExperienceFiles =
          this.oldRelevantExperienceFiles.filter(
            (file) => file.id !== OldFileData?.file?.id
          );
        break;
      }
      case 'keyQualificationsFileControl': {
        this.deletedOldkeyQualificationsFilesIds.push(OldFileData?.file?.id);
        this.oldkeyQualificationsFiles = this.oldkeyQualificationsFiles.filter(
          (file) => file.id !== OldFileData?.file?.id
        );
        break;
      }
      case 'teamCompositionFileControl': {
        this.deletedOldTeamCompositionFilesIds.push(OldFileData?.file?.id);
        this.oldTeamCompositionFiles = this.oldTeamCompositionFiles.filter(
          (file) => file.id !== OldFileData?.file?.id
        );
        break;
      }
      case 'healthAndSafetyFileControl': {
        this.deletedOldHealthAndSafetyFilesIds.push(OldFileData?.file?.id);
        this.oldHealthAndSafetyFiles = this.oldHealthAndSafetyFiles.filter(
          (file) => file.id !== OldFileData?.file?.id
        );
        break;
      }
      case 'qualityAssuranceFileControl': {
        this.deletedOldQualityAssuranceFilesIds.push(OldFileData?.file?.id);
        this.oldQualityAssuranceFiles = this.oldQualityAssuranceFiles.filter(
          (file) => file.id !== OldFileData?.file?.id
        );
        break;
      }
      case 'additionalInformationControl': {
        this.deletedoldAdditionalInformationFilesIds.push(OldFileData?.file?.id);
        this.oldAdditionalInformationFiles = this.oldAdditionalInformationFiles.filter(
          (file) => file.id !== OldFileData?.file?.id
        );
        break;
      }
      default:
        break;
    }
  }
  /**
   * Handling File Operations - END
   */
}
